import { FC, memo, useCallback, useState } from 'react'
import { FileUploader } from 'devextreme-react'
import classNames from 'classnames'
import qs from 'qs'
import { AxiosError } from 'axios'
import { VerifyBulkInsertTasksResponse } from '@crew/apis/task/models/verifyBulkInsertTasks/response'
import { DownloadCsvTemplateForBulkInsertTasksRequest } from '@crew/apis/task/models/downloadCsvTemplateForBulkInsertTasks/request'
import { apiBaseUrl } from '@crew/configs/constants'
import { EntityType } from '@crew/enums/domain'
import { useTranslation } from '@crew/modules/i18n'
import {
  downloadBlob,
  buildApiUrl,
  extractFileNameFromContentDisposition,
} from '@crew/utils'
import { axiosInstance } from 'apis/axiosInstance'
import { useShowApiErrors } from 'hooks/useShowApiErrors'
import { useToast } from 'hooks/useToast'
import { Modal } from 'components/layouts/modal/modal'
import { CrewLink } from 'components/elements/crewLink/crewLink'
import { CrewButton } from 'components/elements/crewButton/crewButton'
import { useTaskImportCsvDialog } from './useTaskImportCsvDialog'
import { ConfirmTaskImportTable } from './components/confirmTaskImportTable/confirmTaskImportTable'

export type TaskImportCsvDialogProps = {
  title: string
  isOpen: boolean
  onClose: () => void

  // CSVインポート対象のプロジェクトID
  projectId: string
}

// タスクCSVインポートダイアログ
export const TaskImportCsvDialog: FC<TaskImportCsvDialogProps> = memo(
  (props) => {
    const { verifyBulkInsertTasks, bulkInsertTasks, isLoadingBulkInsertTasks } =
      useTaskImportCsvDialog(props.projectId)

    const { t } = useTranslation()
    const toast = useToast()
    const [showApiErrors] = useShowApiErrors()

    // アップロードされたファイル名
    const [fileName, setFileName] = useState<string>()

    // CSVファイルの検証結果
    const [verifyBulkInsertTasksResult, setVerifyBulkInsertTasksResult] =
      useState<VerifyBulkInsertTasksResponse>()

    // ダイアログを閉じるときの処理
    const handleCloseDialog = useCallback(() => {
      props.onClose()
      setFileName(undefined)
      setVerifyBulkInsertTasksResult(undefined)
    }, [props])

    // ダウンロードCSVテンプレート
    const handleDownloadCsvTemplate = useCallback(async () => {
      try {
        // バックエンドへのリクエストパラメータを作成
        const requestData: DownloadCsvTemplateForBulkInsertTasksRequest = {
          entityType: EntityType.Project,
          entityRecordId: props.projectId,
        }

        // RTK Queryではなく、axiosInstanceを使用し、fetchを行う
        // RTKQueryは通常JSONレスポンスを想定しており、Blob（バイナリデータ）を適切にシリアライズできないため
        // 開発者ツールのコンソールを見ると、エラー（A non-serializable value was detected.～）が出ている
        const params = qs.stringify(requestData, { arrayFormat: 'repeat' })
        const url = buildApiUrl(
          apiBaseUrl(),
          `api/v1/tasks/bulk-insert/template`
        )
        url.search = params.toString()

        // TODO: 呼び出し側でaxiosInstanceを直接使用しないように、以下タスクでリファクタ予定
        // https://break-tmc.atlassian.net/browse/CREW-16919
        const response = await axiosInstance.get(url.toString(), {
          responseType: 'blob',
        })
        const blob = response.data
        const contentDisposition = response.headers['content-disposition']

        const fileName =
          extractFileNameFromContentDisposition(contentDisposition) ||
          'import_insert_tasks.csv'

        // Blobを使用してダウンロードを実行
        downloadBlob(blob, fileName)
      } catch (error: unknown) {
        // AxiosErrorかどうかをチェック
        if (error instanceof AxiosError) {
          // エラーレスポンスからエラーbodyを取得
          const errorData = error.response?.data
          const errorStatus = error.response?.status

          // エラーデータがBlobの場合（responseType: 'blob'のため）
          if (errorData instanceof Blob) {
            // BlobをJSONに変換してエラー内容を確認
            try {
              const text = await errorData.text()
              const errorJson = JSON.parse(text)
              const errorObject = {
                status: errorStatus,
                data: errorJson,
              }
              showApiErrors(errorObject)
            } catch (parseError) {
              console.error('Error parsing blob as JSON:', parseError)
            }
          }
        } else {
          // その他の予期しないエラーはログに出力しておくのみとする
          console.error('Unexpected error:', error)
        }
      }
    }, [props.projectId, showApiErrors])

    // ファイルアップロード
    const handleFileUpload = useCallback(
      async (file: File) => {
        setFileName(file.name)
        try {
          const result = await verifyBulkInsertTasks(file)

          setVerifyBulkInsertTasksResult(result)
        } catch (error) {
          showApiErrors(error)
        }
      },
      [showApiErrors, verifyBulkInsertTasks]
    )

    // 「タスクを追加」ボタンクリック時の処理
    const handleImportTasksButtonClick = useCallback(async () => {
      try {
        // verifyBulkInsertTasksの結果が存在しない場合は処理しない
        if (!verifyBulkInsertTasksResult) return

        await bulkInsertTasks(verifyBulkInsertTasksResult)
        // Close dialog
        handleCloseDialog()
        toast.success(t('message.task.importCsvSuccess'))
      } catch (error) {
        showApiErrors(error)
      }
    }, [
      bulkInsertTasks,
      handleCloseDialog,
      showApiErrors,
      t,
      toast,
      verifyBulkInsertTasksResult,
    ])

    return (
      <Modal
        title={props.title}
        isOpen={props.isOpen}
        onClose={handleCloseDialog}
      >
        {/* モーダルの最小幅を制限し、画面の各入力項目が正しく表示されるようにする */}
        <div className="p-2.5 flex flex-col gap-y-2.5 min-w-[800px]">
          <p>{t('label.taskImportCsvDialogDescription')}</p>

          {/* CSVファイルのテンプレートをダウンロード */}
          <CrewLink onClick={handleDownloadCsvTemplate}>
            {t('label.downloadCsvTemplate')}
          </CrewLink>

          {/* ファイルドロップ領域 */}
          <div
            id="dropZone"
            className={classNames(
              'h-24 flex items-center justify-center',
              'border-2 border-dashed', // 線
              'bg-crew-gray-1-light dark:bg-crew-gray-4-dark' // 背景色
            )}
          >
            <p>{fileName ?? t('label.dragAndDropCsvFile')}</p>
          </div>

          {/* ファイルアップローダー */}
          <FileUploader
            dropZone="#dropZone"
            visible={false} //カスタムドロップ領域のみ表示するため、FileUploader本体は表示しない
            uploadMode="instantly"
            uploadFile={handleFileUpload}
            dialogTrigger="#dropZone"
            allowedFileExtensions={['.csv']}
            accept="text/csv"
          />

          {/* タスク一覧 */}
          <div className="flex-1 mt-3">
            {
              // CSV内容の確認でエラーが発生した場合にメッセージを表示する
              verifyBulkInsertTasksResult?.errorCount &&
              verifyBulkInsertTasksResult.errorCount > 0 ? (
                <p className="mb-1 text-sm crew-error-text">
                  {t('message.task.importCsvError', {
                    count: verifyBulkInsertTasksResult.errorCount,
                  })}
                </p>
              ) : null
            }

            {/* CSV検証結果の表示 */}
            <ConfirmTaskImportTable
              importTasks={verifyBulkInsertTasksResult?.data ?? []}
            />
          </div>

          <div className="ml-auto flex flex-row gap-x-2.5 items-center">
            {/* タスクを追加ボタン */}
            <CrewButton
              text={t('action.addTask')}
              type="primary"
              onClick={handleImportTasksButtonClick}
              disabled={
                !verifyBulkInsertTasksResult?.data ||
                verifyBulkInsertTasksResult.errorCount > 0 ||
                isLoadingBulkInsertTasks
              }
            />

            {/* キャンセルボタン */}
            <CrewButton
              text={t('action.cancel')}
              type="normal"
              stylingMode="outlined"
              onClick={handleCloseDialog}
            />
          </div>
        </div>
      </Modal>
    )
  }
)
